import React from 'react'
import moment from 'moment'
import { urlRE } from 'src/core/helpers'
import { AppDispatch } from 'src/app/store'
import type { TFunction } from 'next-i18next'
import { setProjectFormItemValue } from 'src/features'
import { ProjectContentImages } from 'src/core/models'
import type { BaseType } from 'antd/lib/typography/Base'
import ProjectFormVideo from './ProjectFormVideo/ProjectFormVideo'
import ProjectFormImages from './ProjectFormImages/ProjectFormImages'
import ProjectFormHeadline from './ProjectFormHeadline/ProjectFormHeadline'
import ProjectFormDescriptions from './ProjectFormDescriptions/ProjectFormDescriptions'
import SdgCompliancesMultiSelect from './SdgCompliancesMultiSelect/SdgCompliancesMultiSelect'
import ProjectFormContentTaxRelief from './ProjectFormContentTaxRelief/ProjectFormContentTaxRelief'
import { CustomTextInput, CustomUrlInput, CustomDatePicker, CustomImageInput } from 'src/components'

interface FormProps {
  name?: string,
  required?: boolean,
  placeholder?: string,
  onChange?: Function
  onChangeFileUrl?: Function
  value?: string
}

interface ProjectFormSectionType {
  isRow?: boolean,
  isOnlyComponent?: boolean,
  rowClassName?: string,
  labels?: {
    title: string,
    subtitle: string,
    thirdLabel?: string,
    thirdLabelType?: BaseType
  }
  formComponent: React.FunctionComponent<any>,
  formProps?: FormProps
}

export const projectFormContentSections =
  (
    t: TFunction,
    dispatch: AppDispatch,
    contentImages: ProjectContentImages,
    isActive: boolean
  ): ProjectFormSectionType[] => [

    { // Project Title
      isRow: true,
      labels: { title: t('Projects.Fields.Title'), subtitle: t('Projects.Form.TitleLabel') },
      formComponent: CustomTextInput,
      formProps: {
        name: 'name',
        required: true,
        placeholder: t('Projects.Form.TitlePlaceholder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(setProjectFormItemValue({ key: 'name', value: e.target.value }))
        },
      }
    },

    { // Project Headline
      isOnlyComponent: true,
      formComponent: ProjectFormHeadline,
    },

    { // Project URL
      isRow: true,
      labels: { title: t('Projects.Fields.Url'), subtitle: t('Projects.Form.UrlLabel') },
      formComponent: CustomUrlInput,
      formProps: {
        name: 'url',
        required: isActive,
        placeholder: t('Projects.Form.UrlPlaceholder'),
        onChange: (value: string) => {
          const url = urlRE.test(value)
          if (url) {
            dispatch(setProjectFormItemValue({ key: 'url', value: value }))
          }
        },
      }
    },

    { // Project SDG Compliances
      isRow: true,
      labels: { title: t('Projects.Form.SDG'), subtitle: t('Projects.Form.SDgLabel') },
      formComponent: SdgCompliancesMultiSelect
    },

    { // Project Descriptions
      isRow: false,
      labels: {
        title: t('Projects.Form.Description'),
        subtitle: t('Projects.Form.DescriptionLabel'),
        thirdLabel: t('Projects.Form.DescriptionSubLabel')
      },
      formComponent: ProjectFormDescriptions
    },

    { // Project Tax Relief Checkbox
      isOnlyComponent: true,
      formComponent: ProjectFormContentTaxRelief,
    },

    { // Project Date Registration
      isRow: true,
      labels: { title: t('Projects.Form.RegistrationDate'), subtitle: t('Projects.Form.RegistrationDateLabel') },
      formComponent: CustomDatePicker,
      formProps: {
        name: 'registered_since',
        required: isActive,
        placeholder: t('admin.CertificateDatePlaceholder'),
        onChange: (value: moment.Moment) => {
          const date = value ? moment(value).format('YYYY-MM-DD') : null
          dispatch(setProjectFormItemValue({ key: 'registered_since', value: date }))
        }
      }
    },

    { // Project Video
      isOnlyComponent: true,
      formComponent: ProjectFormVideo,
    },


    { // Project Cover
      isRow: true,
      rowClassName: 'mt-4',
      labels: {
        title: t('Projects.Form.MainImage'),
        subtitle: t('Projects.Form.MainImageLabel'),
        thirdLabel: t('Projects.FilesAllowed'),
        thirdLabelType: 'danger'
      },
      formComponent: CustomImageInput,
      formProps: {
        name: 'main_image',
        required: isActive,
        placeholder: t('admin.CertificateDatePlaceholder'),
        value: contentImages.main_image || contentImages.cover,
        onChangeFileUrl: (value: string) => {
          dispatch(setProjectFormItemValue({ key: 'main_image', value }))
        }
      }
    },

    { // Project Images
      isRow: true,
      rowClassName: 'mt-4',
      labels: {
        title: t('Projects.Form.Images'),
        subtitle: t('Projects.Form.ImagesLabel'),
        thirdLabel: t('Projects.FilesAllowed'),
        thirdLabelType: 'danger'
      },
      formComponent: ProjectFormImages
    },
  ]
